/**
 * Content Suggestions Panel (Task 3.4)
 * Fetches recommendations from /ml/recommendations and displays them
 * grouped by type: Content, Timing, Format. Handles loading/error states.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Skeleton,
  Alert,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Lightbulb,
  Schedule,
  VideoLibrary,
  Refresh,
  GroupWork,
} from '@mui/icons-material';
import { mlAnalysisAPI } from '../services/api';
import MlEstimateBadge, { ML_ESTIMATE_BASIS } from './MlEstimateBadge';

const DATA_SOURCE_LABELS = {
  top_performers: 'From your data',
  heuristics: 'General best practices',
  llm: 'AI suggestions',
};

function ContentSuggestionsPanel({ clientId, competitorId = null, platform = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [patternBrief, setPatternBrief] = useState(null);
  const [patternLoading, setPatternLoading] = useState(false);
  const [patternError, setPatternError] = useState(null);

  const fetchPatternBrief = useCallback(async () => {
    if (clientId == null) {
      setPatternBrief(null);
      return;
    }
    setPatternLoading(true);
    setPatternError(null);
    try {
      const b = await mlAnalysisAPI.getCompetitorPatternBrief(clientId, { days: 90 });
      setPatternBrief(b);
    } catch (err) {
      setPatternError(
        err.response?.data?.detail || 'Could not load competitor pattern signals',
      );
      setPatternBrief(null);
    } finally {
      setPatternLoading(false);
    }
  }, [clientId]);

  const fetchRecommendations = async () => {
    if (clientId == null && competitorId == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await mlAnalysisAPI.getRecommendations({
        client_id: clientId ?? undefined,
        competitor_id: competitorId ?? undefined,
        platform: platform ?? undefined,
        limit: 100,
        use_llm_fallback: false,
      });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load suggestions');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [clientId, competitorId, platform]);

  useEffect(() => {
    fetchPatternBrief();
  }, [fetchPatternBrief]);

  if (clientId == null && competitorId == null) {
    return (
      <Card sx={{ bgcolor: 'rgba(22, 27, 34, 0.5)', border: '1px solid rgba(48, 54, 61, 0.4)' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Select a client to see content suggestions.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading && !data) {
    return (
      <Card sx={{ bgcolor: 'rgba(22, 27, 34, 0.5)', border: '1px solid rgba(48, 54, 61, 0.4)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading suggestions…</Typography>
          </Box>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="70%" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ bgcolor: 'rgba(22, 27, 34, 0.5)', border: '1px solid rgba(48, 54, 61, 0.4)' }}>
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" startIcon={<Refresh />} onClick={fetchRecommendations}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const rec = data?.recommendations || {};
  const contentItems = rec.content_recommendations || [];
  const timingItems = rec.timing_recommendations || [];
  const formatItems = rec.format_recommendations || [];
  const hasAny = contentItems.length > 0 || timingItems.length > 0 || formatItems.length > 0;
  const dataSource = data?.data_source || 'heuristics';

  return (
    <Card sx={{ bgcolor: 'rgba(22, 27, 34, 0.5)', border: '1px solid rgba(48, 54, 61, 0.4)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lightbulb color="primary" /> Content Suggestions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {dataSource !== 'top_performers' && (
              <MlEstimateBadge
                basis="rule_based"
                tooltip={
                  dataSource === 'llm'
                    ? 'AI-generated suggestions from a structured prompt — review before applying.'
                    : ML_ESTIMATE_BASIS.rule_based
                }
              />
            )}
            <Chip
              size="small"
              label={DATA_SOURCE_LABELS[dataSource] || dataSource}
              variant="outlined"
              sx={{ borderColor: 'rgba(48, 54, 61, 0.6)' }}
            />
          </Box>
        </Box>

        {clientId != null && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid rgba(88, 166, 255, 0.35)', bgcolor: 'rgba(88, 166, 255, 0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}>
                  <GroupWork fontSize="small" color="primary" />
                  Pattern signals (competitors)
                </Typography>
                <MlEstimateBadge basis="pattern_analysis" />
              </Box>
              <Button size="small" startIcon={<Refresh />} onClick={fetchPatternBrief} disabled={patternLoading}>
                Refresh
              </Button>
            </Box>
            <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
              <Typography variant="caption">
                {patternBrief?.disclaimer ||
                  'Aggregated hook, format, length, and cadence from competitor posts — not for copying captions or media.'}
              </Typography>
            </Alert>
            {patternLoading && !patternBrief && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading pattern signals…</Typography>
              </Box>
            )}
            {patternError && (
              <Typography variant="caption" color="error">{patternError}</Typography>
            )}
            {patternBrief && patternBrief.competitor_posts_analyzed > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {patternBrief.narrative_summary}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                  {(patternBrief.hook_types || []).map((h) => (
                    <Chip key={`h-${h}`} size="small" label={`Hook: ${h}`} color="primary" variant="outlined" />
                  ))}
                  {(patternBrief.formats || []).map((f) => (
                    <Chip key={`f-${f}`} size="small" label={`Format: ${f}`} variant="outlined" />
                  ))}
                  {(patternBrief.length_buckets || []).slice(0, 4).map((b) => (
                    <Chip
                      key={`lb-${b.bucket}`}
                      size="small"
                      label={`Length: ${b.bucket} (${Math.round((b.share || 0) * 100)}%)`}
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Cadence: ~{patternBrief.cadence_summary?.approx_posts_per_week ?? '—'} posts/week in window ·
                  peaks { (patternBrief.cadence_summary?.peak_weekdays || []).join(', ') || '—'}
                  {patternBrief.confidence != null && (
                    <> · confidence {Math.round((patternBrief.confidence || 0) * 100)}%</>
                  )}
                </Typography>
              </>
            )}
            {patternBrief && patternBrief.competitor_posts_analyzed === 0 && !patternLoading && (
              <Typography variant="body2" color="text.secondary">
                {patternBrief.narrative_summary || 'No competitor posts with pattern metadata yet. Add competitors and run scrapes.'}
              </Typography>
            )}
          </Box>
        )}

        {!hasAny ? (
          <Typography variant="body2" color="text.secondary">
            No suggestions yet. Add more competitor data or try again later.
          </Typography>
        ) : (
          <Box>
            {contentItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Lightbulb fontSize="small" /> Content
                </Typography>
                <List dense disablePadding>
                  {contentItems.map((item, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Lightbulb fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {timingItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule fontSize="small" /> Timing
                </Typography>
                <List dense disablePadding>
                  {timingItems.map((item, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Schedule fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {formatItems.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VideoLibrary fontSize="small" /> Format
                </Typography>
                <List dense disablePadding>
                  {formatItems.map((item, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <VideoLibrary fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ContentSuggestionsPanel;
